import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { User, Key, Mail, Shield, CreditCard } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { validateEmail, validatePassword } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';

const AccountSettings = () => {
  const { user, setAuthenticatedUser } = useAuth();
  const { t } = useLanguage();
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    username: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [notificationSettings, setNotificationSettings] = useState({
    marketing: true,
    surveys: true, // Changed from security to surveys
    app: true
  });
  const [isSubmittingProfile, setIsSubmittingProfile] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [isSubmittingNotifications, setIsSubmittingNotifications] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        email: user.email || '',
        username: user.username || '',
      });
    }
  }, [user]);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  const handleNotificationChange = (type: 'marketing' | 'surveys' | 'app', checked: boolean) => {
    setNotificationSettings(prev => ({ ...prev, [type]: checked }));
  };

  const handleNotificationsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingNotifications(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success(t('settings.notificationSettingsUpdated'));
    } catch (error) {
      console.error('Error updating notification settings:', error);
      toast.error(t('settings.updateError'));
    } finally {
      setIsSubmittingNotifications(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!profileForm.name || !profileForm.email) {
      toast.error(t('settings.fillRequiredFields'));
      return;
    }
    
    if (!validateEmail(profileForm.email)) {
      toast.error(t('settings.invalidEmail'));
      return;
    }
    
    setIsSubmittingProfile(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update user in context
      if (user) {
        const updatedUser = {
          ...user,
          name: profileForm.name,
          email: profileForm.email,
          username: profileForm.username,
        };
        
        setAuthenticatedUser(updatedUser);
        toast.success(t('settings.profileUpdated'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('settings.updateError'));
    } finally {
      setIsSubmittingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate password
    if (!passwordForm.currentPassword) {
      toast.error(t('settings.enterCurrentPassword'));
      return;
    }
    
    if (!validatePassword(passwordForm.newPassword)) {
      toast.error(t('settings.passwordRequirements'));
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('settings.passwordsDoNotMatch'));
      return;
    }
    
    setIsSubmittingPassword(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success(t('settings.passwordUpdated'));
      
      // Reset password form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error(t('settings.updateError'));
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <p>{t('settings.pleaseLogin')}</p>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto py-6 space-y-8">
      <div className="flex flex-col space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">{t('settings.accountSettings')}</h1>
        <p className="text-muted-foreground">
          {t('settings.accountSettingsDescription')}
        </p>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            {t('settings.profile')}
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Key className="h-4 w-4" />
            {t('settings.security')}
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            {t('settings.notifications')}
          </TabsTrigger>
          <TabsTrigger value="payment" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            {t('settings.payment')}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="profile" className="space-y-4 animate-fade-in">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.personalInformation')}</CardTitle>
              <CardDescription>
                {t('settings.personalInfoDescription')}
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleProfileSubmit}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('form.name')}</Label>
                  <Input
                    id="name"
                    name="name"
                    value={profileForm.name}
                    onChange={handleProfileChange}
                    placeholder={t('form.namePlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={profileForm.email}
                    onChange={handleProfileChange}
                    placeholder={t('form.emailPlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="username">{t('form.username')}</Label>
                  <Input
                    id="username"
                    name="username"
                    value={profileForm.username}
                    onChange={handleProfileChange}
                    placeholder={t('form.usernamePlaceholder')}
                  />
                </div>
              </CardContent>
              <CardFooter className="border-t pt-6">
                <Button type="submit" disabled={isSubmittingProfile}>
                  {isSubmittingProfile ? t('common.saving') : t('common.save')}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </TabsContent>
        
        <TabsContent value="security" className="space-y-4 animate-fade-in">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.changePassword')}</CardTitle>
              <CardDescription>
                {t('settings.passwordSecurityDescription')}
              </CardDescription>
            </CardHeader>
            <form onSubmit={handlePasswordSubmit}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">{t('settings.currentPassword')}</Label>
                  <Input
                    id="currentPassword"
                    name="currentPassword"
                    type="password"
                    showPasswordToggle={true}
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    placeholder="••••••••"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">{t('settings.newPassword')}</Label>
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    showPasswordToggle={true}
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="••••••••"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('settings.confirmPassword')}</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    showPasswordToggle={true}
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="••••••••"
                  />
                </div>
              </CardContent>
              <CardFooter className="border-t pt-6">
                <Button type="submit" disabled={isSubmittingPassword}>
                  {isSubmittingPassword ? t('common.updating') : t('settings.updatePassword')}
                </Button>
              </CardFooter>
            </form>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.accountSecurity')}</CardTitle>
              <CardDescription>
                {t('settings.accountSecurityDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('settings.twoFactorAuth')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.twoFactorAuthDescription')}
                  </p>
                </div>
                <Button variant="outline">{t('settings.setup')}</Button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('settings.loginHistory')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.loginHistoryDescription')}
                  </p>
                </div>
                <Button variant="outline">{t('settings.view')}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="notifications" className="space-y-4 animate-fade-in">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.emailNotifications')}</CardTitle>
              <CardDescription>
                {t('settings.emailNotificationsDescription')}
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleNotificationsSubmit}>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="rounded-lg border p-4 transition-all duration-200 hover:border-primary/50 hover:shadow-sm">
                    <div className="flex flex-col space-y-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-medium">{t('settings.marketingEmails')}</h3>
                          <p className="text-sm text-muted-foreground">
                            {t('settings.marketingEmailsDescription')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            {notificationSettings.marketing ? 
                              t('settings.enabled') : t('settings.disabled')}
                          </span>
                          <Switch
                            checked={notificationSettings.marketing}
                            onCheckedChange={(checked) => handleNotificationChange('marketing', checked)}
                            className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="rounded-lg border p-4 transition-all duration-200 hover:border-primary/50 hover:shadow-sm">
                    <div className="flex flex-col space-y-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-medium">{t('settings.surveysAndFeedback')}</h3>
                          <p className="text-sm text-muted-foreground">
                            {t('settings.surveysAndFeedbackDescription')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            {notificationSettings.surveys ? 
                              t('settings.enabled') : t('settings.disabled')}
                          </span>
                          <Switch
                            checked={notificationSettings.surveys}
                            onCheckedChange={(checked) => handleNotificationChange('surveys', checked)}
                            className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="rounded-lg border p-4 transition-all duration-200 hover:border-primary/50 hover:shadow-sm">
                    <div className="flex flex-col space-y-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-medium">{t('settings.appNotifications')}</h3>
                          <p className="text-sm text-muted-foreground">
                            {t('settings.appNotificationsDescription')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            {notificationSettings.app ? 
                              t('settings.enabled') : t('settings.disabled')}
                          </span>
                          <Switch
                            checked={notificationSettings.app}
                            onCheckedChange={(checked) => handleNotificationChange('app', checked)}
                            className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="border-t pt-6">
                <Button type="submit" disabled={isSubmittingNotifications}>
                  {isSubmittingNotifications ? t('common.saving') : t('common.save')}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </TabsContent>
        
        <TabsContent value="payment" className="space-y-4 animate-fade-in">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.billingHistory')}</CardTitle>
              <CardDescription>
                {t('settings.billingHistoryDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border-b pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{t('settings.premiumSubscription')}</p>
                      <p className="text-sm text-muted-foreground">April 1, 2023</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium">€9.99</span>
                      <Button variant="ghost" size="sm" className="h-8 px-2">
                        {t('settings.viewInvoice')}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="border-b pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{t('settings.premiumSubscription')}</p>
                      <p className="text-sm text-muted-foreground">March 1, 2023</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium">€9.99</span>
                      <Button variant="ghost" size="sm" className="h-8 px-2">
                        {t('settings.viewInvoice')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('settings.subscription')}</CardTitle>
              <CardDescription>
                {t('settings.subscriptionDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border p-4">
                <div className="flex flex-col space-y-2">
                  <h3 className="font-semibold">{t('settings.currentPlan')}: Premium</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.subscriptionRenews')}
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button variant="outline" size="sm">
                    {t('settings.cancelSubscription')}
                  </Button>
                  <Button variant="outline" size="sm">
                    {t('settings.upgradePlan')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AccountSettings;
